import { useState, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { PlusIcon } from '@heroicons/react/24/outline'
import { Input, Modal, SectionPanel, Spinner, Toggle } from '@/components/ui'
import { formatDate } from '@/utils/formatters'

import { useBienContext } from '../BienContext'
import { resetRequestStatus } from '../biensSlice'
import { FORM_TYPES } from '../../mandats/MandatFormContext'
import { setMandatInEdition, resetMandatInEdition } from '../../mandats/mandatsSlice'
import Contact from '../Contact'
import MandatForm from './MandatForm'
import EmptyValuePlaceholder from '../../../components/ui/EmptyValuePlaceholder'

export default () => {
  const dispatch = useDispatch()
  const { bien, 
    loading, isPendingAction,
    updatedSections, markSectionAsUpdated,
  } = useBienContext()
  const [modalVisible, setModalVisible] = useState(false)
  const [animateUpdate, setAnimateUpdate] = useState(false)
  const [formType, setFormType] = useState(FORM_TYPES.EDIT)
  const [formMode, setFormMode] = useState('view') // 'view', 'new_owner', 'edit_mandat', 'add_mandat'

  const SECTION_ID = 'mandat'
  const SECTION_TITLE = "Informations mandat en cours"

  const thisSectionWasUpdated = updatedSections[SECTION_ID]
 
  // Mandats signés (ont une date_signature)
  const signedMandats = bien?.mandats?.filter(m => m.date_signature) || []

  // Mandats non signés (n'ont pas de date_signature)
  const unsignedMandats = bien?.mandats?.filter(m => !m.date_signature) || []

  // Premier mandat non signé (pour édition automatique)
  const firstUnsignedMandat = unsignedMandats[0] || null

  // Types disponibles basés sur les mandats signés uniquement
  const availableTypes = ['location', 'vente'].filter(
    type => !signedMandats.some(m => m.type_mandat === type)
  )

  useEffect(() => {
    setAnimateUpdate(!!thisSectionWasUpdated)
    const timer = setTimeout(() => {
      setAnimateUpdate(false)
    }, 2000) // Durée de l'animation

    return () => clearTimeout(timer)
  }, [thisSectionWasUpdated])

  const { dialogRef } = Modal.useModal()
  
  const NewOwnerButton = () => (
    <button
      className="btn btn-sm btn-primary"
      onClick={() => {
        setFormMode('new_owner')

        if (firstUnsignedMandat) {
          // Édition du mandat existant non signé
          setFormType(FORM_TYPES.EDIT)
          dispatch(setMandatInEdition(firstUnsignedMandat))
        } else {
          // Création d'un nouveau mandat
          setFormType(FORM_TYPES.NEW)
          dispatch(setMandatInEdition({ bien_id: bien.id }))
        }
        
        setModalVisible(true)
      }}
    >
      <PlusIcon className="h-5 w-5 mr-1"/> Arrivée propriétaire
    </button>
  )

  const AddMandatButton = ({ type }) => {
    const mandatToEdit = bien.mandats.find(m => m.type_mandat === type) ||  { type_mandat: type, bien_id: bien.id }

    return (
      <button 
        className="btn btn-sm btn-primary"
        onClick={() => {
          setFormMode('add_mandat')
          setFormType(FORM_TYPES.NEW)
          dispatch(setMandatInEdition(mandatToEdit))
          setModalVisible(true)
        }}
      >
        <PlusIcon className="h-5 w-5 mr-1"/> {mandatToEdit.id ? "Modifier" : "Ajouter"} mandat {type}
      </button>
    )
  }

  // Logique d'affichage des boutons selon l'état
  const renderActionButtons = () => {
    if(loading) {
      return null
    }

    // Cas 1: Aucun mandat OU tout premier mandat non signé
    if (!bien?.mandats?.length || (bien.mandats.length == 1 && firstUnsignedMandat) || (bien.mandats.every( m => !!m.date_fin ))) {
      return <NewOwnerButton />
    }

    // Cas 2: Tous les mandats sont signés
    // Afficher seulement les boutons pour créer les types restants
    return (
      <div className="flex space-x-2">
        {availableTypes.map(type => (
          <AddMandatButton key={type} type={type} />
        ))}
      </div>
    )
  }

  // Pour le bouton Annuler du formulaire
  const handleFormCancel = () => {
    dispatch(resetRequestStatus())
    dispatch(resetMandatInEdition())
    setModalVisible(false)
  }

  // Effectuer une transition fluide à la fermeture du modal
  const handleFormSuccess = () => {
    if (markSectionAsUpdated) {
      markSectionAsUpdated(SECTION_ID)
    }
    dispatch(resetMandatInEdition())
    setModalVisible(false)
  }

  const mandatLocation = useMemo(() => {
    if(bien?.mandats) {
      return bien.mandats.find( m => m.type_mandat === "location" )
    }
    return null
  }, [bien?.mandats])

  const mandatVente = useMemo(() => {
    if(bien?.mandats) {
      return bien.mandats.find( m => m.type_mandat === "vente" )
    }
    return null
  }, [bien?.mandats])

  return (
    <>
      <SectionPanel>
        <SectionPanel.Title title={SECTION_TITLE}>
          <div className="flex justify-end mb-4">
            {renderActionButtons()}
          </div>
        </SectionPanel.Title>
        <SectionPanel.Body columns={3} animateUpdate={animateUpdate}>
          <div className="space-y-4">            
            { !loading && bien.proprietaires?.length > 0 && bien.proprietaires.map((proprietaire, index) => (
              <Contact key={proprietaire.id} item={proprietaire} index={index} />
            ))}
            { (!bien.proprietaires || bien.proprietaires?.length == 0 ) && (
              <Input.Display label="Propriétaires">
                { loading && isPendingAction && (
                    <Spinner color='primary'/>
                )}
              </Input.Display>
            )}
            <Toggle.Display label="Clé dispo à l'agence" value={bien.cle_dispo} />
            { bien?.remise_cles_file ?
              <Input.DisplayFile label="Remise de clé" title="Remise de clé" item={bien} name="remise_cles_file" /> :
              <Input.Display label="Remise de clé" />
            }
          </div>

          {/* { (bien?.mandat && bien.mandats.length > 0) && */}
          <div className="space-y-2">
            <h4 className="text-gray-600 font-bold">Mandat de Location</h4>
            { mandatLocation ? 
                <>
                  <Input.Display label="N° mandat" value={mandatLocation.numero}/>
                  <Input.Display label="Date début mandat" value={formatDate(mandatLocation.date_signature)} />
                  <Input.Display label="Date fin mandat" value={formatDate(mandatLocation.date_fin)} />
                  <Input.DisplayFile label="Mandat" title="le mandat" item={mandatLocation} name="mandat_file" /> 
                </> :
                <>
                  <EmptyValuePlaceholder />
                </>
            }
          </div>

          <div className="space-y-2">
            <h4 className="text-gray-600 font-bold">Mandat de Vente</h4>
            { mandatVente ? 
                <>
                  <Input.Display label="N° mandat" value={mandatVente.numero}/>
                  <Input.Display label="Date début mandat" value={formatDate(mandatVente.date_signature)} />
                  <Input.Display label="Date fin mandat" value={formatDate(mandatVente.date_fin)} />
                  <Toggle.Display label="Exclusivité" value={mandatVente.exclusivite} />
                  <Input.DisplayFile label="Mandat" title="le mandat" item={mandatVente} name="mandat_file" /> 
                </> :
                <>
                  <EmptyValuePlaceholder />
                </>
            }
          </div>
          {/* } */}
        </SectionPanel.Body>
      </SectionPanel>

      {/* Modal pour afficher soit le formulaire de modification du bien, soit le formulaire de création de propriétaire */}
      <Modal
        open={modalVisible}
        onClose={handleFormCancel}
        ref={dialogRef}
        backdrop={false}
        className="max-w-full rounded-none overflow-hidden"
      >
        <Modal.Header showCloseButton={false} />
        <Modal.Body className="overflow-visible min-h-[400px]">
          <MandatForm
            mode={formMode}
            formType={formType}
            onClose={handleFormCancel}
            onSuccess={handleFormSuccess}
          />
        </Modal.Body>
      </Modal>
    </>
  )
}